import {
  AddressInfo,
  BusinessPartnerBody,
  MigrationCandidateBusinessPartnerListBody as PmMigrationCandidateBusinessPartnerListBody,
  MigrationCandidateMatchTarget,
  MigrationCandidateUserListBody,
  PaginatedMigrationCandidateBusinessPartnerListBody,
} from '../api/generated/pm';

/**
 * Builds a candidate's source-system address.
 *
 * `stateProvinceRegion` is a required field of `addressInfo` and is prefilled
 * into the Add-as-new-customer form, so it must carry a real value here — a
 * blank left the State/Province input empty while every sibling field
 * populated, which reads as an app bug rather than a fixture gap.
 */
const mockAddress = (
  street: string,
  zip: string,
  city: string,
  stateProvinceRegion: string,
  country: string
): AddressInfo => ({
  addressLineOne: street,
  stateProvinceRegion,
  zipPostalCode: zip,
  city,
  country,
});

/**
 * Builds an Exact Match's linked `businessPartner`. `extId` must be a Topcon
 * Resource Name whose digits form a valid 12-digit organization number (see
 * `isValidOrganizationNumber`) so the card's/Connect wizard's number tag
 * renders instead of a blank pill. Typed against `BusinessPartnerBody` so a
 * missing required field (e.g. `companyAddress`, `createdAt`, `type`) is a
 * compile error rather than a blank field discovered in the UI.
 */
const mockBusinessPartner = (
  id: string,
  name: string,
  extId: string,
  companyAddress: AddressInfo,
  createdAt: string
): BusinessPartnerBody => ({
  id,
  name,
  extId,
  type: 'organization',
  companyAddress,
  createdAt,
});

/**
 * Builds a `MigrationCandidateMatchTarget` for the ReviewOrganizationModal's
 * "Potential matches" tab. `addressScore`/`nameScore`/`domainScore` drive the
 * per-dimension match/partial/unmatched icons shown in the score tooltip;
 * `score` drives the overall match-percentage badge.
 */
const mockMatchTarget = (
  externalId: string,
  name: string,
  street: string,
  zip: string,
  city: string,
  stateProvinceRegion: string,
  countryCode: string,
  score: number,
  addressScore: number,
  nameScore: number,
  domainScore: number
): MigrationCandidateMatchTarget => ({
  external_id: externalId,
  name,
  business_address_line_1: street,
  business_address_zip_postal_code: zip,
  business_address_city: city,
  business_address_state_province_region: stateProvinceRegion,
  business_address_country_code: countryCode,
  score,
  address_score: addressScore,
  name_score: nameScore,
  domain_score: domainScore,
});

export const migrationCandidateBusinessPartnersMockData: PmMigrationCandidateBusinessPartnerListBody =
  {
    items: [
      {
        id: 'mock-candidate-1',
        name: 'Bob & Sons Co.',
        sourceId: 'TLM-10001',
        sourceSystem: 'Topnet Live Manager',
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-01-15T00:00:00Z',
        modifiedAt: '2025-01-15T00:00:00Z',
        dealerBusinessPartnerId: 'urn:topcon:md::986907144682:bp', // TREE COMPANY Co. S.A.-
        address: {
          addressLineOne: '100 Main St',
          city: 'Portland',
          country: 'US',
          stateProvinceRegion: 'OR',
          zipPostalCode: '97201',
        },
        matchDetails: {
          score: 92,
          score_gap_1_2: 35,
          top_targets: [
            // Fully matched: address, name and domain all score high.
            mockMatchTarget(
              'urn:topcon:md::448307705001:bp',
              'Bob & Sons Co.',
              '100 Main St',
              '97201',
              'Portland',
              'OR',
              'US',
              92,
              95,
              98,
              90
            ),
            // Partial match: name is close but address/domain are weaker.
            mockMatchTarget(
              'urn:topcon:md::448307705002:bp',
              'Bob and Sons Company',
              '450 Riverside Dr',
              '97205',
              'Portland',
              'OR',
              'US',
              57,
              40,
              85,
              45
            ),
            // Unmatched: name is similar in spelling only, everything else fails.
            mockMatchTarget(
              'urn:topcon:md::448307705003:bp',
              'Bobby Sons Trucking',
              '12 Industrial Way',
              '73301',
              'Austin',
              'TX',
              'US',
              18,
              5,
              40,
              10
            ),
          ],
        },
      },
      {
        id: 'mock-candidate-2',
        name: 'Bob & Sons Rental Co.',
        sourceId: 'TLM-10002',
        sourceSystem: 'Topnet Live Manager',
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-01-20T00:00:00Z',
        modifiedAt: '2025-01-20T00:00:00Z',
        address: {
          addressLineOne: '200 Rental Ave',
          city: 'Portland',
          country: 'US',
          stateProvinceRegion: 'OR',
          zipPostalCode: '97202',
        },
        matchDetails: {
          score: 61,
          score_gap_1_2: null,
          top_targets: [
            // Single, borderline partial match to exercise the one-target case.
            mockMatchTarget(
              'urn:topcon:md::448307705004:bp',
              'Bob & Sons Rentals',
              '205 Rental Ave',
              '97202',
              'Portland',
              'OR',
              'US',
              61,
              75,
              80,
              28
            ),
          ],
        },
      },
      {
        id: 'mock-candidate-3',
        name: 'Sub-Dealer Name',
        sourceId: 'TLM-10003',
        sourceSystem: 'Topnet Live Manager',
        dealerBusinessPartnerId: 'mock-candidate-2',
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-02-01T00:00:00Z',
        modifiedAt: '2025-02-01T00:00:00Z',
        address: {
          addressLineOne: '300 Sub St',
          city: 'Portland',
          country: 'US',
          stateProvinceRegion: 'OR',
          zipPostalCode: '97203',
        },
      },
      {
        id: 'mock-candidate-4',
        name: 'Sub-Rental Dealer Name',
        sourceId: 'TLM-10004',
        sourceSystem: 'Topnet Live Manager',
        dealerBusinessPartnerId: 'mock-candidate-2',
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-02-05T00:00:00Z',
        modifiedAt: '2025-02-05T00:00:00Z',
        address: {
          addressLineOne: '400 Rental Sub Rd',
          city: 'Portland',
          country: 'US',
          stateProvinceRegion: 'OR',
          zipPostalCode: '97204',
        },
      },
      {
        id: 'mock-candidate-5',
        name: 'Sub-Dealer Name',
        sourceId: 'TLM-10005',
        sourceSystem: 'Topnet Live Manager',
        dealerBusinessPartnerId: 'mock-candidate-2',
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-02-10T00:00:00Z',
        modifiedAt: '2025-02-10T00:00:00Z',
        address: {
          addressLineOne: '500 Dealer Ln',
          city: 'Portland',
          country: 'US',
          stateProvinceRegion: 'OR',
          zipPostalCode: '97205',
        },
      },
      {
        id: 'mock-candidate-6',
        name: 'Precision Engineering Equipment LLC',
        sourceId: 'TLM-10006',
        sourceSystem: 'Topnet Live Manager',
        // Real Business Partner id (not a `mock-*` fixture id) so that the "Add as
        // new organization" flow's Migration Candidate Dealer card resolves to an
        // actual dealer name/address from `/v1/providers` (the list
        // `CompanyDivisionModal` fetches via `useAvailableAgreements`), instead of
        // rendering no card. Update this if the referenced dealer BP ever stops
        // appearing in that list in the target environment.
        dealerBusinessPartnerId: '83c3db26-2ad5-4a5b-be86-41e316a347c1', // 4 RIVERS EQUIPMENT, LLC-EL PASO
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-03-01T00:00:00Z',
        modifiedAt: '2025-03-01T00:00:00Z',
        address: {
          addressLineOne: '600 Engineering Blvd',
          city: 'Denver',
          country: 'US',
          stateProvinceRegion: 'CO',
          zipPostalCode: '80202',
        },
      },
    ],
  };

/**
 * Stress-test fixture for `SuggestedUsersModal` (used via
 * `VITE_FF_MIGRATION_MOCK_DATA`). A handful of hand-picked edge-case rows up
 * front (name-derivation separators, very long/short local-parts, non-pending
 * statuses that the modal must filter out) followed by a generated bulk set
 * to exercise scrolling / select-all / table perf with a large list.
 */
export const migrationCandidateUsersMockData: MigrationCandidateUserListBody = {
  items: [
    {
      id: 'mock-user-1',
      email: 'john.doe@bobsons.com',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-01-15T00:00:00Z',
      modifiedAt: '2025-01-15T00:00:00Z',
    },
    {
      id: 'mock-user-2',
      email: 'jane.smith@bobsons.com',
      profiles: ['site_manager'],
      status: 'pending',
      createdAt: '2025-01-20T00:00:00Z',
      modifiedAt: '2025-01-20T00:00:00Z',
    },
    {
      id: 'mock-user-3',
      email: 'mike.johnson@bobsons.com',
      profiles: ['bp_admin'],
      status: 'accepted',
      createdAt: '2025-02-01T00:00:00Z',
      modifiedAt: '2025-02-01T00:00:00Z',
    },
    // --- edge cases -----------------------------------------------------
    {
      id: 'mock-user-edge-1',
      email: 'a@b.com', // single-character local-part
      profiles: [],
      status: 'pending',
      createdAt: '2025-01-01T00:00:00Z',
      modifiedAt: '2025-01-01T00:00:00Z',
    },
    {
      id: 'mock-user-edge-2',
      email: 'ALL_CAPS-Mixed.Separators_Name@example.com', // exercises every deriveNameFromEmail separator + case-folding
      profiles: [
        'bp_admin',
        'site_manager',
        'field_tech',
        'billing_viewer',
        'equipment_operator',
        'read_only',
      ],
      status: 'pending',
      createdAt: '2025-01-02T00:00:00Z',
      modifiedAt: '2025-01-02T00:00:00Z',
    },
    {
      id: 'mock-user-edge-3',
      email:
        'extraordinarily.long.local.part.designed.to.wrap.or.overflow.the.name.and.email.table.cells@a-very-long-subdomain.example-corporation.com',
      profiles: ['bp_admin'],
      status: 'pending',
      createdAt: '2025-01-03T00:00:00Z',
      modifiedAt: '2025-01-03T00:00:00Z',
    },
    {
      id: 'mock-user-edge-4',
      email: '12345.67890@example.com', // numeric-only local-part
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-01-04T00:00:00Z',
      modifiedAt: '2025-01-04T00:00:00Z',
    },
    {
      id: 'mock-user-edge-5',
      email: 'jos\u00e9.m\u00fcller@caf\u00e9-r\u00e9sum\u00e9.example', // unicode local-part + domain
      profiles: ['site_manager'],
      status: 'pending',
      createdAt: '2025-01-05T00:00:00Z',
      modifiedAt: '2025-01-05T00:00:00Z',
    },
    {
      id: 'mock-user-edge-6',
      email: 'noseparatorlocalpart@example.com', // no ./_/- to split on at all
      profiles: ['bp_admin'],
      status: 'rejected', // should never render: non-pending is filtered out
      createdAt: '2025-01-06T00:00:00Z',
      modifiedAt: '2025-01-06T00:00:00Z',
    },
    {
      id: 'mock-user-edge-7',
      email: 'plus+addressing.tag@example.com', // '+' is not a deriveNameFromEmail separator
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-01-07T00:00:00Z',
      modifiedAt: '2025-01-07T00:00:00Z',
    },
    // --- generated bulk set (60 rows; most 'pending', a few sprinkled
    // 'accepted'/'rejected' to confirm the visible-list filter still holds
    // at volume) ---------------------------------------------------------
    {
      id: 'mock-user-b1',
      email: 'john.doe@acme.co',
      profiles: ['read_only', 'equipment_operator'],
      status: 'pending',
      createdAt: '2025-02-02T00:00:00Z',
      modifiedAt: '2025-02-02T00:00:00Z',
    },
    {
      id: 'mock-user-b2',
      email: 'jane.smith@example.org',
      profiles: ['bp_admin', 'site_manager', 'field_tech'],
      status: 'pending',
      createdAt: '2025-03-03T00:00:00Z',
      modifiedAt: '2025-03-03T00:00:00Z',
    },
    {
      id: 'mock-user-b3',
      email: 'mike.johnson@globex.io',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-04-04T00:00:00Z',
      modifiedAt: '2025-04-04T00:00:00Z',
    },
    {
      id: 'mock-user-b4',
      email: 'sarah.connor@initech.net',
      profiles: ['read_only', 'equipment_operator'],
      status: 'pending',
      createdAt: '2025-05-05T00:00:00Z',
      modifiedAt: '2025-05-05T00:00:00Z',
    },
    {
      id: 'mock-user-b5',
      email: 'david.brown@umbrella-corp.com',
      profiles: ['bp_admin', 'site_manager', 'field_tech'],
      status: 'pending',
      createdAt: '2025-06-06T00:00:00Z',
      modifiedAt: '2025-06-06T00:00:00Z',
    },
    {
      id: 'mock-user-b6',
      email: 'emily.miller@contoso.test',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-07-07T00:00:00Z',
      modifiedAt: '2025-07-07T00:00:00Z',
    },
    {
      id: 'mock-user-b7',
      email: 'chris.davis@bobsons.com',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-08-08T00:00:00Z',
      modifiedAt: '2025-08-08T00:00:00Z',
    },
    {
      id: 'mock-user-b8',
      email: 'anna.wilson@acme.co',
      profiles: ['bp_admin', 'site_manager', 'field_tech'],
      status: 'pending',
      createdAt: '2025-09-09T00:00:00Z',
      modifiedAt: '2025-09-09T00:00:00Z',
    },
    {
      id: 'mock-user-b9',
      email: 'robert.anderson@example.org',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-10-10T00:00:00Z',
      modifiedAt: '2025-10-10T00:00:00Z',
    },
    {
      id: 'mock-user-b10',
      email: 'laura.thomas@globex.io',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-11-11T00:00:00Z',
      modifiedAt: '2025-11-11T00:00:00Z',
    },
    {
      id: 'mock-user-b11',
      email: 'kevin.moore@initech.net',
      profiles: ['bp_admin', 'site_manager', 'field_tech'],
      status: 'pending',
      createdAt: '2025-12-12T00:00:00Z',
      modifiedAt: '2025-12-12T00:00:00Z',
    },
    {
      id: 'mock-user-b12',
      email: 'megan.jackson@umbrella-corp.com',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-01-13T00:00:00Z',
      modifiedAt: '2025-01-13T00:00:00Z',
    },
    {
      id: 'mock-user-b13',
      email: 'brian.martin@contoso.test',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-02-14T00:00:00Z',
      modifiedAt: '2025-02-14T00:00:00Z',
    },
    {
      id: 'mock-user-b14',
      email: 'nicole.lewis@bobsons.com',
      profiles: ['read_only', 'equipment_operator', 'billing_viewer'],
      status: 'pending',
      createdAt: '2025-03-15T00:00:00Z',
      modifiedAt: '2025-03-15T00:00:00Z',
    },
    {
      id: 'mock-user-b15',
      email: 'steven.hall@acme.co',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-04-16T00:00:00Z',
      modifiedAt: '2025-04-16T00:00:00Z',
    },
    {
      id: 'mock-user-b16',
      email: 'amanda.allen@example.org',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-05-17T00:00:00Z',
      modifiedAt: '2025-05-17T00:00:00Z',
    },
    {
      id: 'mock-user-b17',
      email: 'paul.young@globex.io',
      profiles: ['read_only', 'equipment_operator', 'billing_viewer'],
      status: 'accepted',
      createdAt: '2025-06-18T00:00:00Z',
      modifiedAt: '2025-06-18T00:00:00Z',
    },
    {
      id: 'mock-user-b18',
      email: 'rachel.wright@initech.net',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-07-19T00:00:00Z',
      modifiedAt: '2025-07-19T00:00:00Z',
    },
    {
      id: 'mock-user-b19',
      email: 'mark.scott@umbrella-corp.com',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-08-20T00:00:00Z',
      modifiedAt: '2025-08-20T00:00:00Z',
    },
    {
      id: 'mock-user-b20',
      email: 'jessica.green@contoso.test',
      profiles: ['read_only', 'equipment_operator', 'billing_viewer'],
      status: 'pending',
      createdAt: '2025-09-21T00:00:00Z',
      modifiedAt: '2025-09-21T00:00:00Z',
    },
    {
      id: 'mock-user-b21',
      email: 'daniel.adams@bobsons.com',
      profiles: ['bp_admin'],
      status: 'pending',
      createdAt: '2025-10-22T00:00:00Z',
      modifiedAt: '2025-10-22T00:00:00Z',
    },
    {
      id: 'mock-user-b22',
      email: 'karen.nelson@acme.co',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-11-23T00:00:00Z',
      modifiedAt: '2025-11-23T00:00:00Z',
    },
    {
      id: 'mock-user-b23',
      email: 'james.carter@example.org',
      profiles: ['read_only', 'equipment_operator', 'billing_viewer'],
      status: 'rejected',
      createdAt: '2025-12-24T00:00:00Z',
      modifiedAt: '2025-12-24T00:00:00Z',
    },
    {
      id: 'mock-user-b24',
      email: 'linda.mitchell@globex.io',
      profiles: ['bp_admin'],
      status: 'pending',
      createdAt: '2025-01-25T00:00:00Z',
      modifiedAt: '2025-01-25T00:00:00Z',
    },
    {
      id: 'mock-user-b25',
      email: 'thomas.roberts@initech.net',
      profiles: ['read_only', 'equipment_operator'],
      status: 'pending',
      createdAt: '2025-02-26T00:00:00Z',
      modifiedAt: '2025-02-26T00:00:00Z',
    },
    {
      id: 'mock-user-b26',
      email: 'patricia.turner@umbrella-corp.com',
      profiles: ['read_only', 'equipment_operator', 'billing_viewer'],
      status: 'pending',
      createdAt: '2025-03-27T00:00:00Z',
      modifiedAt: '2025-03-27T00:00:00Z',
    },
    {
      id: 'mock-user-b27',
      email: 'charles.phillips@contoso.test',
      profiles: ['bp_admin'],
      status: 'pending',
      createdAt: '2025-04-28T00:00:00Z',
      modifiedAt: '2025-04-28T00:00:00Z',
    },
    {
      id: 'mock-user-b28',
      email: 'barbara.parker@bobsons.com',
      profiles: ['read_only', 'equipment_operator'],
      status: 'pending',
      createdAt: '2025-05-01T00:00:00Z',
      modifiedAt: '2025-05-01T00:00:00Z',
    },
    {
      id: 'mock-user-b29',
      email: 'joseph.evans@acme.co',
      profiles: ['read_only', 'equipment_operator', 'billing_viewer'],
      status: 'pending',
      createdAt: '2025-06-02T00:00:00Z',
      modifiedAt: '2025-06-02T00:00:00Z',
    },
    {
      id: 'mock-user-b30',
      email: 'elizabeth.edwards@example.org',
      profiles: ['bp_admin'],
      status: 'pending',
      createdAt: '2025-07-03T00:00:00Z',
      modifiedAt: '2025-07-03T00:00:00Z',
    },
    {
      id: 'mock-user-b31',
      email: 'andrew.stewart@globex.io',
      profiles: ['read_only', 'equipment_operator'],
      status: 'pending',
      createdAt: '2025-08-04T00:00:00Z',
      modifiedAt: '2025-08-04T00:00:00Z',
    },
    {
      id: 'mock-user-b32',
      email: 'susan.osullivan@initech.net',
      profiles: ['bp_admin', 'site_manager', 'field_tech'],
      status: 'pending',
      createdAt: '2025-09-05T00:00:00Z',
      modifiedAt: '2025-09-05T00:00:00Z',
    },
    {
      id: 'mock-user-b33',
      email: 'ryan.vanderberg@umbrella-corp.com',
      profiles: ['bp_admin'],
      status: 'pending',
      createdAt: '2025-10-06T00:00:00Z',
      modifiedAt: '2025-10-06T00:00:00Z',
    },
    {
      id: 'mock-user-b34',
      email: 'margaret.alrashid@contoso.test',
      profiles: ['read_only', 'equipment_operator'],
      status: 'accepted',
      createdAt: '2025-11-07T00:00:00Z',
      modifiedAt: '2025-11-07T00:00:00Z',
    },
    {
      id: 'mock-user-b35',
      email: 'eric.yamamotowatanabeextremelylongsurnamefortesting@bobsons.com',
      profiles: ['bp_admin', 'site_manager', 'field_tech'],
      status: 'pending',
      createdAt: '2025-12-08T00:00:00Z',
      modifiedAt: '2025-12-08T00:00:00Z',
    },
    {
      id: 'mock-user-b36',
      email: 'dorothy.nguyentran@acme.co',
      profiles: ['bp_admin'],
      status: 'pending',
      createdAt: '2025-01-09T00:00:00Z',
      modifiedAt: '2025-01-09T00:00:00Z',
    },
    {
      id: 'mock-user-b37',
      email: 'jacob.garcialopez@example.org',
      profiles: ['read_only', 'equipment_operator'],
      status: 'pending',
      createdAt: '2025-02-10T00:00:00Z',
      modifiedAt: '2025-02-10T00:00:00Z',
    },
    {
      id: 'mock-user-b38',
      email: 'lisa.kowalski@globex.io',
      profiles: ['bp_admin', 'site_manager', 'field_tech'],
      status: 'pending',
      createdAt: '2025-03-11T00:00:00Z',
      modifiedAt: '2025-03-11T00:00:00Z',
    },
    {
      id: 'mock-user-b39',
      email: 'gary.papadopoulos@initech.net',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-04-12T00:00:00Z',
      modifiedAt: '2025-04-12T00:00:00Z',
    },
    {
      id: 'mock-user-b40',
      email: 'nancy.doe@umbrella-corp.com',
      profiles: ['read_only', 'equipment_operator'],
      status: 'pending',
      createdAt: '2025-05-13T00:00:00Z',
      modifiedAt: '2025-05-13T00:00:00Z',
    },
    {
      id: 'mock-user-b41',
      email: 'zephyrxin.johnson@contoso.test',
      profiles: ['bp_admin', 'site_manager', 'field_tech'],
      status: 'pending',
      createdAt: '2025-06-14T00:00:00Z',
      modifiedAt: '2025-06-14T00:00:00Z',
    },
    {
      id: 'mock-user-b42',
      email: 'obrien.connor@bobsons.com',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-07-15T00:00:00Z',
      modifiedAt: '2025-07-15T00:00:00Z',
    },
    {
      id: 'mock-user-b43',
      email: 'almansouri.lee@acme.co',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-08-16T00:00:00Z',
      modifiedAt: '2025-08-16T00:00:00Z',
    },
    {
      id: 'mock-user-b44',
      email: 'jeanluc.brown@example.org',
      profiles: ['bp_admin', 'site_manager', 'field_tech'],
      status: 'pending',
      createdAt: '2025-09-17T00:00:00Z',
      modifiedAt: '2025-09-17T00:00:00Z',
    },
    {
      id: 'mock-user-b45',
      email: 'bjorn.davis@globex.io',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-10-18T00:00:00Z',
      modifiedAt: '2025-10-18T00:00:00Z',
    },
    {
      id: 'mock-user-b46',
      email: 'renee.garcia@initech.net',
      profiles: ['bp_admin', 'site_manager'],
      status: 'rejected',
      createdAt: '2025-11-19T00:00:00Z',
      modifiedAt: '2025-11-19T00:00:00Z',
    },
    {
      id: 'mock-user-b47',
      email: 'muller.wilson@umbrella-corp.com',
      profiles: ['bp_admin', 'site_manager', 'field_tech'],
      status: 'pending',
      createdAt: '2025-12-20T00:00:00Z',
      modifiedAt: '2025-12-20T00:00:00Z',
    },
    {
      id: 'mock-user-b48',
      email: 'nguyen.thomas@contoso.test',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-01-21T00:00:00Z',
      modifiedAt: '2025-01-21T00:00:00Z',
    },
    {
      id: 'mock-user-b49',
      email: 'soren.taylor@bobsons.com',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-02-22T00:00:00Z',
      modifiedAt: '2025-02-22T00:00:00Z',
    },
    {
      id: 'mock-user-b50',
      email: 'ismail.moore@acme.co',
      profiles: ['read_only', 'equipment_operator', 'billing_viewer'],
      status: 'pending',
      createdAt: '2025-03-23T00:00:00Z',
      modifiedAt: '2025-03-23T00:00:00Z',
    },
    {
      id: 'mock-user-b51',
      email: 'john.martin@example.org',
      profiles: ['read_only'],
      status: 'accepted',
      createdAt: '2025-04-24T00:00:00Z',
      modifiedAt: '2025-04-24T00:00:00Z',
    },
    {
      id: 'mock-user-b52',
      email: 'jane.lewis@globex.io',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-05-25T00:00:00Z',
      modifiedAt: '2025-05-25T00:00:00Z',
    },
    {
      id: 'mock-user-b53',
      email: 'mike.walker@initech.net',
      profiles: ['read_only', 'equipment_operator', 'billing_viewer'],
      status: 'pending',
      createdAt: '2025-06-26T00:00:00Z',
      modifiedAt: '2025-06-26T00:00:00Z',
    },
    {
      id: 'mock-user-b54',
      email: 'sarah.hall@umbrella-corp.com',
      profiles: ['read_only'],
      status: 'pending',
      createdAt: '2025-07-27T00:00:00Z',
      modifiedAt: '2025-07-27T00:00:00Z',
    },
    {
      id: 'mock-user-b55',
      email: 'david.young@contoso.test',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-08-28T00:00:00Z',
      modifiedAt: '2025-08-28T00:00:00Z',
    },
    {
      id: 'mock-user-b56',
      email: 'emily.king@bobsons.com',
      profiles: ['read_only', 'equipment_operator', 'billing_viewer'],
      status: 'pending',
      createdAt: '2025-09-01T00:00:00Z',
      modifiedAt: '2025-09-01T00:00:00Z',
    },
    {
      id: 'mock-user-b57',
      email: 'chris.wright@acme.co',
      profiles: ['bp_admin'],
      status: 'pending',
      createdAt: '2025-10-02T00:00:00Z',
      modifiedAt: '2025-10-02T00:00:00Z',
    },
    {
      id: 'mock-user-b58',
      email: 'anna.green@example.org',
      profiles: ['bp_admin', 'site_manager'],
      status: 'pending',
      createdAt: '2025-11-03T00:00:00Z',
      modifiedAt: '2025-11-03T00:00:00Z',
    },
    {
      id: 'mock-user-b59',
      email: 'robert.baker@globex.io',
      profiles: ['read_only', 'equipment_operator', 'billing_viewer'],
      status: 'pending',
      createdAt: '2025-12-04T00:00:00Z',
      modifiedAt: '2025-12-04T00:00:00Z',
    },
    {
      id: 'mock-user-b60',
      email: 'laura.adams@initech.net',
      profiles: ['bp_admin'],
      status: 'pending',
      createdAt: '2025-01-05T00:00:00Z',
      modifiedAt: '2025-01-05T00:00:00Z',
    },
  ],
};

// Exact Match rows: the candidate's own (source-system) address and its linked
// Business Partner's `companyAddress` are the same real-world place, so both
// fields share one literal per row rather than repeating the street/zip/city.
const cs6Address = mockAddress('987 Cedar Ln', '98101', 'Seattle', 'WA', 'US');
const cs7Address = mockAddress('111 Beach Rd', '33101', 'Miami', 'FL', 'US');
const cs8Address = mockAddress('222 Mountain Way', '80201', 'Denver', 'CO', 'US');
const cs9Address = mockAddress('333 Harbor St', '02101', 'Boston', 'MA', 'US');

export const migrationCandidateCustomerSuggestionsMockData: PaginatedMigrationCandidateBusinessPartnerListBody =
  {
    page: 1,
    limit: 50,
    total: 9,
    items: [
      // REVIEW — no exact match, but ranked top targets: the dealer reviews the
      // potential matches or adds the suggestion as a new customer.
      {
        id: 'cs-1',
        name: 'Acme Construction Ltd',
        sourceSystem: 'Topnet Live Manager',
        sourceId: 'TLM-001',
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-03-01T00:00:00Z',
        modifiedAt: '2025-03-01T00:00:00Z',
        address: mockAddress('123 Main St', '10001', 'New York', 'NY', 'US'),
        matchDetails: {
          score: 94,
          score_gap_1_2: 31,
          top_targets: [
            mockMatchTarget(
              'urn:topcon:md::448307705123:bp',
              'Acme Construction Limited',
              '123 Main St',
              '10001',
              'New York',
              'NY',
              'US',
              94,
              96,
              97,
              88
            ),
            mockMatchTarget(
              'urn:topcon:md::448307705234:bp',
              'Acme Constructors Inc',
              '77 Broadway',
              '10006',
              'New York',
              'NY',
              'US',
              63,
              45,
              82,
              35
            ),
            mockMatchTarget(
              'urn:topcon:md::448307705345:bp',
              'Acme Landscaping Co',
              '9 Cypress Rd',
              '33101',
              'Miami',
              'FL',
              'US',
              21,
              8,
              44,
              12
            ),
          ],
        },
      },
      // REVIEW — no matches at all: an empty target list, so Review offers only
      // "add as new customer".
      {
        id: 'cs-2',
        name: 'BuildRight Solutions',
        sourceSystem: 'Topnet Live Manager',
        sourceId: 'TLM-002',
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-03-05T00:00:00Z',
        modifiedAt: '2025-03-05T00:00:00Z',
        address: mockAddress('456 Oak Ave', '90210', 'Los Angeles', 'CA', 'US'),
        matchDetails: {
          score: 0,
          score_gap_1_2: null,
          top_targets: [],
        },
      },
      // REVIEW — cs-3 through cs-5: no `matchDetails` at all (the import found
      // nothing worth ranking), so Review offers only "add as new customer".
      {
        id: 'cs-3',
        name: 'Precision Surveying Inc',
        sourceSystem: 'Topnet Live Manager',
        sourceId: 'TLM-003',
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-03-10T00:00:00Z',
        modifiedAt: '2025-03-10T00:00:00Z',
        address: mockAddress('789 Pine Rd', '60601', 'Chicago', 'IL', 'US'),
      },
      {
        id: 'cs-4',
        name: 'GeoTech Engineering',
        sourceSystem: 'Topnet Live Manager',
        sourceId: 'TLM-004',
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-03-15T00:00:00Z',
        modifiedAt: '2025-03-15T00:00:00Z',
        address: mockAddress('321 Elm Blvd', '30301', 'Atlanta', 'GA', 'US'),
      },
      {
        id: 'cs-5',
        name: 'Summit Infrastructure',
        sourceSystem: 'Topnet Live Manager',
        sourceId: 'TLM-005',
        status: 'pending',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-03-20T00:00:00Z',
        modifiedAt: '2025-03-20T00:00:00Z',
        address: mockAddress('654 Maple Dr', '75201', 'Dallas', 'TX', 'US'),
      },
      // CONNECT — exact match: already linked to a Business Partner with the
      // Service Agreement still pending, so `needsConnect` sends Review straight
      // to the Connect wizard. There is nothing left to choose.
      {
        id: 'cs-6',
        name: 'Metro Building Corp',
        sourceSystem: 'Topnet Live Manager',
        sourceId: 'TLM-006',
        businessPartner: mockBusinessPartner(
          'bp-existing-1',
          'Metro Building Corp',
          'urn:topcon:md::448307706001:bp',
          cs6Address,
          '2025-03-25T00:00:00Z'
        ),
        status: 'matched',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-03-25T00:00:00Z',
        modifiedAt: '2025-03-25T00:00:00Z',
        address: cs6Address,
      },
      // CONNECT — exact match, same shape as cs-6: linked Business Partner, SA
      // still pending, no `matchDetails`.
      {
        id: 'cs-7',
        name: 'Coastal Contractors',
        sourceSystem: 'Topnet Live Manager',
        sourceId: 'TLM-007',
        businessPartner: mockBusinessPartner(
          'bp-existing-2',
          'Coastal Contractors',
          'urn:topcon:md::448307706002:bp',
          cs7Address,
          '2025-04-01T00:00:00Z'
        ),
        status: 'matched',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-04-01T00:00:00Z',
        modifiedAt: '2025-04-01T00:00:00Z',
        address: cs7Address,
      },
      // CONNECT — exact match, same shape as cs-6.
      {
        id: 'cs-8',
        name: 'Highland Development Group',
        sourceSystem: 'Topnet Live Manager',
        sourceId: 'TLM-008',
        businessPartner: mockBusinessPartner(
          'bp-existing-3',
          'Highland Development Group',
          'urn:topcon:md::448307706003:bp',
          cs8Address,
          '2025-04-05T00:00:00Z'
        ),
        status: 'matched',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-04-05T00:00:00Z',
        modifiedAt: '2025-04-05T00:00:00Z',
        address: cs8Address,
      },
      // CONNECT — the CSSD-connect-status-fix regression: the Dealer already
      // Merged this suggestion in an earlier session (`status: 'accepted'`,
      // not `'matched'`) and left before finishing Connect. The Service
      // Agreement is still pending, and the backend never cleared the stale
      // `matchDetails.top_targets` it carried from before the Merge —
      // `needsConnect` must route this to Connect without reading that field.
      {
        id: 'cs-9',
        name: 'Riverside Fabrication',
        sourceSystem: 'Topnet Live Manager',
        sourceId: 'TLM-009',
        businessPartner: mockBusinessPartner(
          'bp-existing-4',
          'Riverside Fabrication',
          'urn:topcon:md::448307706004:bp',
          cs9Address,
          '2025-04-10T00:00:00Z'
        ),
        status: 'accepted',
        serviceAgreementStatus: 'pending',
        createdAt: '2025-04-10T00:00:00Z',
        modifiedAt: '2025-04-10T00:00:00Z',
        address: cs9Address,
        matchDetails: {
          score: 88,
          score_gap_1_2: 22,
          top_targets: [
            mockMatchTarget(
              'urn:topcon:md::448307706004:bp',
              'Riverside Fabrication',
              '333 Harbor St',
              '02101',
              'Boston',
              'MA',
              'US',
              88,
              90,
              92,
              80
            ),
          ],
        },
      },
    ],
  };
